import React from 'react';
import {
  Card,
  CardHeader,
  Text,
  Button,
  Badge,
  Avatar,
  AvatarGroup,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  MoreHorizontal24Regular,
  Archive24Regular,
  Delete24Regular,
  CheckmarkCircle24Filled,
  Comment24Regular,
  Attach24Regular,
} from '@fluentui/react-icons';
import { KanbanCard } from '../../types/kanban';
import { AvatarColor } from './avatarUtils';

const useStyles = makeStyles({
  card: {
    marginBottom: '1rem',
    cursor: 'pointer',
    borderRadius: '8px',
    userSelect: 'none' as any,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow2,
    transition: 'all 0.2s ease',
    position: 'relative',
    '&:hover': {
      boxShadow: tokens.shadow8,
      transform: 'translateY(-1px)',
      border: `1px solid ${tokens.colorBrandStroke1}`,
    },
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  priorityBadge: {
    fontSize: '12px',
    flexShrink: 0,
    alignSelf: 'flex-start',
  },
  assignees: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  cardDeleteButton: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    opacity: 0,
    transition: 'opacity 0.2s ease',
    zIndex: 2,
    backgroundColor: 'transparent',
    borderRadius: '4px',
    padding: '4px',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  cardDescription: {
    fontSize: '14px',
    lineHeight: '18px',
    margin: 0,
    padding: 0,
    width: '100%',
    display: '-webkit-box !important' as any,
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical' as any,
    overflow: 'hidden',
    color: tokens.colorNeutralForeground1,
  },
});

interface TaskCardProps {
  card: KanbanCard;
  isReadOnly: boolean;
  isHovered: boolean;
  userPhotos: Record<string, string>;
  getAvatarColor: (userId: string) => AvatarColor;
  onCardClick: (card: KanbanCard) => void;
  onArchive: (card: KanbanCard) => void;
  onDelete: (card: KanbanCard) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

const getPriorityColor = (priority: string): string => {
  switch (priority) {
    case 'high': return 'danger';
    case 'medium': return 'warning';
    case 'low': return 'success';
    default: return 'informative';
  }
};

export const TaskCard: React.FC<TaskCardProps> = ({
  card,
  isReadOnly,
  isHovered,
  userPhotos,
  getAvatarColor,
  onCardClick,
  onArchive,
  onDelete,
  onMouseEnter,
  onMouseLeave,
}) => {
  const styles = useStyles();

  return (
    <Card
      className={styles.card}
      onClick={() => onCardClick(card)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        cursor: isReadOnly ? 'default' : 'pointer',
      }}
    >
      {!isReadOnly && (
        <Menu positioning="below-end">
          <MenuTrigger disableButtonEnhancement>
            <Button
              appearance="subtle"
              size="small"
              icon={<MoreHorizontal24Regular />}
              onClick={(e) => {
                e.stopPropagation();
              }}
              title="Task actions"
              className={styles.cardDeleteButton}
              style={{
                opacity: isHovered ? 1 : 0,
              }}
            />
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              <MenuItem
                icon={<Archive24Regular />}
                onClick={(e) => {
                  e.stopPropagation();
                  onArchive(card);
                }}
              >
                Archive
              </MenuItem>
              <MenuItem
                icon={<Delete24Regular />}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(card);
                }}
              >
                Delete
              </MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>
      )}

      {card.color && card.color !== '' && (
        <div
          style={{
            height: '10px',
            backgroundColor: card.color,
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px',
            margin: '-16px -16px 4px -16px'
          }}
        />
      )}

      <CardHeader
        header={
          <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS }}>
            <Badge
              appearance="filled"
              color={getPriorityColor(card.priority) as any}
              className={styles.priorityBadge}
            >
              {card.priority.toUpperCase()}
            </Badge>
            <Text weight="semibold" size={400}>{card.title}</Text>
          </div>
        }
      />

      <div className={styles.cardContent}>
        {card.description && (
          <p className={styles.cardDescription} style={{ fontSize: '14px', lineHeight: '18px' }}>
            {card.description}
          </p>
        )}

        {(card.startDate || card.endDate) && (
          <div style={{
            display: 'flex',
            gap: '0.5rem',
            fontSize: '13px',
            color: tokens.colorNeutralForeground2,
            marginTop: '6px',
            fontWeight: '500'
          }}>
            {card.startDate && (
              <span>{new Date(card.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}</span>
            )}
            {card.startDate && card.endDate && <span>-</span>}
            {card.endDate && (
              <span>{new Date(card.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}</span>
            )}
          </div>
        )}

        {((card.assignedTo && card.assignedTo.length > 0) ||
          (card.checklist && card.checklist.length > 0) ||
          (card.comments && card.comments.length > 0) ||
          (card.attachments && card.attachments.length > 0)) && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            {card.assignedTo && card.assignedTo.length > 0 ? (
              <div className={styles.assignees}>
                <AvatarGroup layout="spread">
                  {card.assignedTo.slice(0, 2).map((user) => (
                    <Avatar
                      key={user.id}
                      image={userPhotos[user.id] ? { src: userPhotos[user.id] } : undefined}
                      name={user.displayName}
                      color={getAvatarColor(user.id)}
                      size={24}
                    />
                  ))}
                </AvatarGroup>
                {card.assignedTo.length > 2 && (
                  <Text size={200}>+{card.assignedTo.length - 2}</Text>
                )}
              </div>
            ) : <div />}
            {((card.checklist && card.checklist.length > 0) ||
              (card.comments && card.comments.length > 0) ||
              (card.attachments && card.attachments.length > 0)) && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                color: tokens.colorNeutralForeground2,
                fontSize: '13px',
                fontWeight: '500'
              }}>
                {card.checklist && card.checklist.length > 0 && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}>
                    <CheckmarkCircle24Filled style={{
                      fontSize: '16px',
                      color: card.checklist.every((item: any) => item.completed)
                        ? tokens.colorPaletteGreenForeground1
                        : tokens.colorNeutralForeground2
                    }} />
                    <span>{card.checklist.filter((item: any) => item.completed).length}/{card.checklist.length}</span>
                  </div>
                )}
                {card.attachments && card.attachments.length > 0 && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}>
                    <Attach24Regular style={{ fontSize: '16px' }} />
                    <span>{card.attachments.length}</span>
                  </div>
                )}
                {card.comments && card.comments.length > 0 && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}>
                    <Comment24Regular style={{ fontSize: '16px' }} />
                    <span>{card.comments.length}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};
